<template>
  <div>
    <KTCodePreview :title="pageTitle">
      <template v-slot:toolbar>
        <b-button
          variant="outline-primary"
          size="sm"
          class="font-weight-bolder mr-2"
          @click="onClickDownloadDocx"
          :disabled="!isReadyPrint || isDownloadingDocx || !supportsDocx"
          v-b-tooltip.hover
          :title="supportsDocx ? 'Tải file Word (.docx)' : 'Chế độ này chưa hỗ trợ xuất DOCX'"
        >
          <b-spinner v-if="isDownloadingDocx" small class="mr-1"></b-spinner>
          <i
            v-else
            style="font-size: 1rem"
            class="far fa-file-word"
          ></i>
          {{ isDownloadingDocx ? 'Đang tải...' : 'Tải Word' }}
        </b-button>
        <b-button
          variant="outline-success"
          size="sm"
          class="font-weight-bolder mr-2"
          @click="onClickDownloadExcel"
          :disabled="!isReadyPrint || isDownloadingExcel"
        >
          <b-spinner v-if="isDownloadingExcel" small class="mr-1"></b-spinner>
          <i
            v-else
            style="font-size: 1rem"
            class="far fa-file-excel"
          ></i>
          {{ isDownloadingExcel ? 'Đang tải xuống...' : 'Tải Excel' }}
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          class="font-weight-bolder"
          @click="onClickPrint"
          :disabled="!isReadyPrint"
        >
          <i style="font-size: 1rem" class="fas fa-print"></i>
          In
        </b-button>
      </template>
      <template v-slot:preview>
        <div id="print-document-content">
          <PrintBillWholesaleInvoice
            v-if="mode === 'bb-bghh'"
            :printData="{ bill }"
          />
          <PrintBillHopDong
            v-else-if="mode === 'hop-dong'"
            :printData="{ bill }"
          />
          <PrintBillPhuLucImei
            v-else-if="mode === 'phu-luc-imei'"
            :printData="{ bill }"
          />
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { convertNumberToText, handleError } from '@/utils/common';
import fileDownload from '@/utils/file-download';
import { BILL_ITEM_TYPE, AMOUNT_TYPE } from '@/utils/enum';
import { SUPPLIER_INFO, TIME_TRIGGER } from '@/utils/constants';
import PrintBillWholesaleInvoice from '@/view/components/bills/PrintBillWholesaleInvoice.vue';
import PrintBillHopDong from '@/view/components/bills/PrintBillHopDong.vue';
import PrintBillPhuLucImei from '@/view/components/bills/PrintBillPhuLucImei.vue';

export default {
  name: 'PrintBillDocuments',
  components: {
    KTCodePreview,
    PrintBillWholesaleInvoice,
    PrintBillHopDong,
    PrintBillPhuLucImei,
  },
  data() {
    return {
      bill: null,
      isReadyPrint: false,
      isDownloadingExcel: false,
      isDownloadingDocx: false,
      SUPPLIER_INFO,
    };
  },
  computed: {
    mode() {
      return this.$route.query.mode || 'bb-bghh';
    },
    pageTitle() {
      const titles = {
        'bb-bghh': 'In biên bản bàn giao hàng hóa',
        'hop-dong': 'In hợp đồng mua bán',
        'phu-luc-imei': 'In phụ lục danh sách IMEI',
      };
      return titles[this.mode] || 'In tài liệu';
    },
    excelFileName() {
      const names = {
        'bb-bghh': 'bien-ban-giao-hang',
        'hop-dong': 'hop-dong-mua-ban',
        'phu-luc-imei': 'phu-luc-imei',
      };
      const billNumber = this.bill?.billNumber || '';
      return `${names[this.mode] || 'tai-lieu'}-${billNumber}`;
    },
    supportsDocx() {
      return this.mode === 'bb-bghh';
    },
  },
  async created() {
    const billNumber = this.$route.query.id;
    await this.getBillById(billNumber);
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hóa đơn bán buôn', route: '/wholesale-invoice' },
      { title: this.pageTitle },
    ]);
  },
  methods: {
    async getBillById(billNumber) {
      this.isReadyPrint = false;
      await ApiService.get(`v1/bills/${billNumber}`).then((response) => {
        const { data } = response.data;
        this.bill = this.mappingBill(data);
        setTimeout(() => {
          this.isReadyPrint = true;
        }, 1200);
      });
    },
    mappingBill(data) {
      const totalPay = this.calcTotalPay(data);
      const amountConvertText = convertNumberToText(totalPay);
      const amountTextUpperCase = amountConvertText.charAt(1).toUpperCase() + amountConvertText.slice(2).toLowerCase();
      const totalAmountText = convertNumberToText(data.totalAmount || 0);
      const textTotalAmount = totalAmountText.charAt(1).toUpperCase() + totalAmountText.slice(2).toLowerCase();
      const billCriteria = this.calcSumCriteria(data);
      return {
        listBillItem: this.mappingBillItems(data.billItems),
        billNumber: data.billNumber,
        storeId: data.storeId,
        storeName: data.storeName,
        storeAddress: data.storeAddress,
        storeHotline: data.storeHotline,
        customerName: data.customerName,
        customerPhone: data.customerPhone,
        totalAmount: data.totalAmount,
        orderNote: data.customerNote,
        saleName: data.saleName,
        cashierName: data.cashierName,
        createdAt: data.createdAt,
        warrantyNote: data.warrantyNote,
        depositAmount: data.depositAmount,
        transferFromOrder: data.transferFromOrder,
        customerPoint: data.customerPoint,
        moneyInstallment: data.moneyInstallment,
        moneyCredit: data.moneyCredit,
        subtractPoint: data.subtractPoint || 0,
        pointUse: data.pointToMoney || 0,
        totalItemAmount: data.totalItemAmount,
        discountValue: data.discountValue,
        customerAddress: data.customerBillAddress,
        totalPay,
        textAmount: amountTextUpperCase,
        customerShipFee: data.customerShipFee,
        conversionFee: data.conversionFee,
        sumQuantity: billCriteria.quantity,
        sumTotalDiscount: billCriteria.discount,
        sumUnitAmount: billCriteria.unitAmount,
        sumTotalAmount: billCriteria.totalAmount,
        customerCompanyName: data.customerCompanyName,
        customerTaxCode: data.customerTaxCode || data.taxCode || '',
        customerBillAddress: data.customerBillAddress,
        supplierAddress: data.supplierAddress,
        receiverAddress: data.receiverAddress,
        receiverMobile: data.receiverMobile,
        receiverName: data.receiverName,
        customerRepresentativeName: data.customerRepresentativeName,
        customerRepresentativeJobTitle: data.customerRepresentativeJobTitle,
        textTotalAmount,
        einvoice: data.einvoice,
      };
    },
    calcTotalPay(data) {
      const sum = ['depositAmount', 'transferFromOrder', 'moneyCredit', 'moneyInstallment', 'pointToMoney']
        .reduce((result, item) => result + (data[item] || 0), 0);
      return data.totalAmount - sum + (data.customerShipFee || 0) + (data.conversionFee || 0);
    },
    calcSumCriteria(data) {
      const result = { quantity: 0, discount: 0, unitAmount: 0, totalAmount: 0 };
      if (!data?.billItems) return result;
      data.billItems.forEach((item) => {
        result.quantity += item.quantity;
        if (item.billItemType === BILL_ITEM_TYPE.PRODUCT) {
          if (item.discountType === AMOUNT_TYPE.MONEY && item.discountAmount > 0) {
            result.discount += item.discountAmount;
          } else if (item.discountType === AMOUNT_TYPE.PERCENT && item.discountAmount > 0) {
            result.discount += ((item.discountAmount * item.productPrice) / 100) * item.quantity;
          }
          result.unitAmount += item.productPrice * item.quantity;
        }
      });
      result.totalAmount = result.unitAmount - result.discount;
      return result;
    },
    mappingBillItems(billItems) {
      let stt = 0;
      return billItems.reduce((result, element) => {
        if (element.billItemType === BILL_ITEM_TYPE.PRODUCT) stt++;
        const productPrice = element.productPrice || 0;
        let discountItemAmount = element.discountAmount;
        const discountType = Number(element.discountType) || AMOUNT_TYPE.MONEY;
        if (discountType === AMOUNT_TYPE.PERCENT && element.discountAmount <= 100) {
          discountItemAmount = (productPrice * element.discountAmount) / 100;
        }
        result.push({
          stt,
          id: element.productId,
          name: element.productName,
          code: element.productCode,
          price: productPrice,
          type: element.productType,
          quantity: element.quantity,
          imeiCode: element.imeiCode,
          totalPrice: element.quantity * productPrice,
          discount: discountItemAmount,
          billItemType: element.billItemType,
          discountType,
          productUnit: element.productUnit,
          warrantyMonthNo: element.warrantyMonthNo,
        });
        return result;
      }, []);
    },
    onClickPrint() {
      const printNode = document.getElementById('print-document-content');
      if (!printNode) return;
      const prtHtml = printNode.outerHTML;
      let stylesHtml = '';
      for (const node of [...document.querySelectorAll('link[rel="stylesheet"], style')]) {
        stylesHtml += node.outerHTML;
      }
      const stylePrint = '<sty' + 'le>' + `
        @media print { @page { size: A4; margin: 10mm; } }
        .font-size-print { font-size: 12px !important; color: black; }
        .print-bb-title { font-size: 30px !important; color: black; font-weight: bold !important; }
        .font-size-title { font-size: 15px !important; color: black; }
        .font-size-gift { font-size: 11px !important; color: black; }
        .print-uom, .print-qty { font-size: 9px !important; line-height: 1.05 !important; }
        .table-bordered-print th, .table-bordered-print td {
          border: 0.5px solid #474545 !important;
          padding: 3px 5px !important;
        }
        .table-bordered-print th { text-align: center !important; vertical-align: middle !important; }
        .text-center { text-align: center !important; }
        .text-right { text-align: right !important; }
        .text-left { text-align: left !important; }
        .imei-cell p { overflow-wrap: break-word !important; white-space: normal !important; line-height: 1.4 !important; }
      ` + '</sty' + 'le>';
      const WinPrint = window.open('', '', 'left=0,top=0,width=900,height=900,toolbar=0,scrollbars=0,status=0');
      WinPrint.document.write(`<!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            ${stylesHtml}
            ${stylePrint}
          </head>
          <body>${prtHtml}</body>
        </html>`);
      WinPrint.document.close();
      WinPrint.focus();
      setTimeout(() => WinPrint.print(), TIME_TRIGGER);
    },
    onClickDownloadDocx() {
      if (!this.bill || this.isDownloadingDocx || !this.supportsDocx) return;
      this.downloadBillDocument('docx');
    },
    async downloadBillDocument(format) {
      const isDocx = format === 'docx';
      const loadingKey = isDocx ? 'isDownloadingDocx' : 'isDownloadingExcel';
      if (this[loadingKey]) return;

      const billNumber = this.bill.billNumber;
      this[loadingKey] = true;
      try {
        const response = await ApiService.get('v1/bills/export-document', {
          params: { billNumber, mode: this.mode, format },
          responseType: 'blob',
        });
        const fallbackExt = isDocx ? 'docx' : 'xlsx';
        const fileName =
          fileDownload.getFileName(response) || `${this.excelFileName}.${fallbackExt}`;
        fileDownload.createFile(
          response.data,
          fileName,
          isDocx
            ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            : undefined,
        );
      } catch (err) {
        try {
          const text = await err.response?.data?.text();
          const json = JSON.parse(text);
          err.response.data = json;
        } catch (_) {}
        handleError(err);
      } finally {
        this[loadingKey] = false;
      }
    },
    async onClickDownloadExcel() {
      if (!this.bill || this.isDownloadingExcel) return;
      await this.downloadBillDocument('xlsx');
    },
  },
};
</script>
